<script setup lang="ts">
/*
 * Mirrors the prototype's .btn-primary / .btn-secondary / .btn-amber /
 * .btn-ghost-pill class set:
 *   primary      → bg primary, white, 12px radius, 14px text, brand shadow
 *   secondary    → white surface, slate text, 1px app-border
 *   amber        → soft amber for "needs attention" affordances
 *   ghost-pill   → small primary-tinted pill ("Resume payroll" style)
 *   outline      → transparent fill, currentColor border + text, hover tint;
 *                  inherits its colour from the parent (set `text-*` on the
 *                  element) so it reads as a low-emphasis bordered action.
 *
 * Padding/radius matches the prototype CSS exactly. Disabled state collapses
 * the brand shadow per the prototype's :disabled rule.
 *
 * Leading / trailing icons go in the `#iconLeft` / `#iconRight` slots; the base
 * flex + gap spaces them from the label automatically.
 */
import { computed } from 'vue';
import { Loader2 } from 'lucide-vue-next';

type Variant = 'primary' | 'secondary' | 'amber' | 'ghost-pill' | 'danger' | 'outline';
type Size = 'xs' | 'sm' | 'md';

const props = withDefaults(
  defineProps<{
    variant?: Variant;
    size?: Size;
    disabled?: boolean;
    /**
     * In-flight state for the action this button triggers (bind a TanStack
     * mutation's `isPending`). While true the button shows an inline spinner,
     * is treated as disabled (no double-submit), and exposes `aria-busy`. The
     * label is kept in the layout (opacity-0) so the width never jumps.
     */
    loading?: boolean;
    type?: 'button' | 'submit' | 'reset';
    fullWidth?: boolean;
  }>(),
  { variant: 'primary', size: 'md', type: 'button' },
);

defineEmits<{ click: [ev: MouseEvent] }>();

// `loading` implies disabled: it blocks the native click + the disabled styles.
const isDisabled = computed(() => props.disabled || props.loading);
// Spinner sizes track the button size so it sits centred without reflow.
const spinnerSize = computed(() => (props.size === 'xs' ? 14 : props.size === 'sm' ? 16 : 18));

const base =
  'relative inline-flex items-center justify-center gap-2 font-medium whitespace-nowrap select-none cursor-pointer transition-colors disabled:cursor-not-allowed';

const sizing = computed(() =>
  props.size === 'xs'
    ? 'gap-1.5 px-2.5 py-0.5 text-xs rounded-lg' // matches Badge height (px-2.5 py-0.5 text-xs)
    : props.size === 'sm'
      ? 'px-3 py-1.5 text-md rounded-lg'
      : 'px-4 py-2.5 text-base rounded-xl',
);

const variants: Record<Variant, string> = {
  primary:
    'bg-primary text-white shadow-btn-primary hover:bg-primary-600 disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none',
  secondary:
    'bg-app-surface text-slate-700 border border-app-border hover:bg-slate-50 disabled:text-slate-400 disabled:bg-app-surface',
  amber: 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100',
  'ghost-pill':
    'bg-primary-50 text-primary-700 border border-primary-100 rounded-pill px-3.5 py-2 text-md hover:bg-primary-100',
  danger:
    'bg-red-600 text-white hover:bg-red-700 disabled:bg-red-200 disabled:text-red-400 disabled:shadow-none',
  // No text-current here: the caller sets the colour via a `text-*` class, and the
  // border follows it through `border-current`. Including `text-current` would
  // compete with the caller's class and can fall back to the inherited (dark) colour
  // — which showed the outline border/text as near-black.
  outline: 'bg-transparent border border-current hover:bg-current/10 disabled:opacity-50',
};
</script>

<template>
  <button
    :type="type"
    :class="[
      base,
      variant === 'ghost-pill' ? '' : sizing,
      variants[variant],
      fullWidth ? 'w-full' : '',
    ]"
    :disabled="isDisabled"
    :aria-busy="loading ? 'true' : undefined"
    @click="$emit('click', $event)"
  >
    <!--
      While loading, the label/icons stay in the layout (opacity-0) so the
      button width never jumps, and a centred spinner overlays them.
    -->
    <span
      :class="['inline-flex items-center gap-2', loading ? 'opacity-0' : '']"
      :aria-hidden="loading ? 'true' : undefined"
    >
      <slot name="iconLeft" />
      <slot />
      <slot name="iconRight" />
    </span>
    <span
      v-if="loading"
      class="absolute inset-0 flex items-center justify-center"
      data-testid="button-spinner"
    >
      <Loader2 :size="spinnerSize" class="animate-spin" aria-hidden="true" />
    </span>
  </button>
</template>
